/**
 * Termeni si conditii + politica de confidentialitate.
 *
 * Continutul de aici nu e umplutura: reflecta exact deciziile pe care le
 * implementeaza restul aplicatiei si care ii sunt opozabile clientului.
 *
 *  - ADR-003: vanzatorul e restaurantul, platforma intermediaza;
 *  - ADR-004: taxa de livrare e linie pe bonul restaurantului, TVA standard;
 *  - ADR-005: numai numerar la livrare, incasat de curier ca mandatar;
 *  - ADR-015: calificarea GDPR e MIXTA — nu se scrie nicaieri ca restaurantul
 *    ar fi doar imputernicit, fiindca datele de pe bon raman la el sub retentie
 *    fiscala si nu pot fi sterse la cerere;
 *  - ADR-017: la refuz, comanda si bonul se intorc la restaurant;
 *  - ADR-018: facturarea pe firma e a restaurantului, nu a platformei.
 *
 * Ce NU face: nu inventeaza identitatea operatorului platformei. Denumirea, CUI-ul
 * si sediul vin din `config.identity.legalNotice`; daca piata nu le-a configurat,
 * lipsa se spune pe pagina. Un document legal cu date inventate e mai rau decat
 * unul incomplet.
 */

import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";

import { useBootstrap } from "../components/BootstrapProvider";
import { Alert, SectionHeading } from "../components/ui";

function Article({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      <div className="flex flex-col gap-2 text-sm text-ink">{children}</div>
    </section>
  );
}

export function Legal() {
  const { document: documentSlug } = useParams<{ document?: string }>();
  const { bootstrap, config, t } = useBootstrap();
  // Lista vine CALCULATA de la server, din firmele chiar listate pe piață. O
  // enumerare scrisă de mână ar diverge tăcut de cine primește efectiv datele,
  // iar divergența ar fi exact între ce scrie aici și realitate.
  const destinatariGrup = bootstrap.recipients ?? [];
  const isPrivacy = documentSlug === "confidentialitate";
  const platformName = config.identity.name;

  const contact =
    config.identity.supportEmail ?? config.identity.supportPhone ?? null;

  return (
    <div className="mx-auto flex max-w-readable flex-col gap-6">
      <nav aria-label="Documente legale" className="flex gap-2">
        <Link
          to="/legal"
          className={`rounded-control px-3 py-2 text-sm font-medium ${isPrivacy ? "text-ink-muted" : "bg-brand-soft text-ink"}`}
        >
          Termeni și condiții
        </Link>
        <Link
          to="/legal/confidentialitate"
          className={`rounded-control px-3 py-2 text-sm font-medium ${isPrivacy ? "bg-brand-soft text-ink" : "text-ink-muted"}`}
        >
          Confidențialitate
        </Link>
      </nav>

      {config.identity.legalNotice === undefined || config.identity.legalNotice.length === 0 ? (
        <Alert tone="warning" title="Datele operatorului platformei nu sunt configurate">
          Denumirea, CUI-ul și sediul firmei care operează platforma nu au fost completate pentru această
          piață, deci nu apar mai jos.
        </Alert>
      ) : (
        <p className="text-sm text-ink-muted">{config.identity.legalNotice}</p>
      )}

      {isPrivacy ? (
        <div className="flex flex-col gap-6">
          <SectionHeading level={1}>Politica de confidențialitate</SectionHeading>

          <Article title="Ce date prelucrăm">
            <p>
              Numele, numărul de telefon, adresa de livrare și indicațiile pentru curier, adresa de e-mail
              dacă o completezi, conținutul comenzii și, la cerere, datele de facturare ale firmei tale.
            </p>
          </Article>

          <Article title="De ce le prelucrăm">
            <p>
              Pentru a prelua comanda, a o transmite restaurantului, a o livra și a răspunde reclamațiilor.
              Temeiul este executarea contractului. Pentru e-mailurile de ofertă, temeiul este acordul tău,
              pe care îl poți retrage oricând.
            </p>
          </Article>

          <Article title="Cine mai primește datele și în ce calitate">
            <p>
              <strong>Restaurantul care pregătește comanda</strong> primește datele necesare executării:
              numele, telefonul, adresa și conținutul comenzii. Pentru această parte, restaurantul
              prelucrează la instrucțiunea {platformName}, pe baza unui acord de prelucrare.
            </p>
            <p>
              Aceleași date ajung însă și în <strong>documentele fiscale și în arhiva de vânzări</strong> ale
              restaurantului — bon, factură, jurnale contabile. Pentru acea parte restaurantul este{" "}
              <strong>operator independent</strong>, cu temei propriu: obligația legală de a emite și de a
              păstra documentele. Aceste înregistrări se păstrează conform legii fiscale, în general 10 ani,
              și <strong>nu pot fi șterse la cerere</strong>.
            </p>
            <p>
              <strong>Curierul</strong> primește numele, telefonul și adresa, strict cât timp are comanda în
              livrare.
            </p>
            {destinatariGrup.length > 1 && (
              <>
                <p>
                  Restaurantele din această piață aparțin unui <strong>grup de firme</strong>. Fiecare este
                  operator independent pe propriile documente, deci datele tale nu circulă între ele doar
                  fiindcă fac parte din același grup.
                </p>
                <p>
                  Dacă bifezi, la finalizarea comenzii, că vrei să primești oferte, acordul acoperă firmele
                  de mai jos — și numai pe ele. Le enumerăm aici ca să știi de la început cine poate fi
                  destinatarul, iar dacă lista se schimbă te întrebăm din nou.
                </p>
                <ul>
                  {destinatariGrup.map((firma) => (
                    <li key={firma.taxId}>
                      {firma.legalName} <span className="opacity-70">(CUI {firma.taxId})</span>
                    </li>
                  ))}
                </ul>
                <p>
                  Executarea comenzii rămâne separată: datele necesare livrării merg doar la restaurantul
                  care gătește, indiferent dacă ai bifat sau nu.
                </p>
              </>
            )}
          </Article>

          <Article title="Ștergere și anonimizare">
            <p>
              Poți cere ștergerea datelor din contul și din istoricul platformei. Acolo unde datele fac parte
              dintr-un document fiscal, ele nu se șterg: documentul rămâne, iar noi anonimizăm ce se poate
              anonimiza fără să afectăm valabilitatea lui. Îți spunem explicit ce s-a șters și ce a rămas.
            </p>
          </Article>

          <Article title="Drepturile tale">
            <p>
              Ai dreptul de acces, rectificare, ștergere în limitele de mai sus, restricționare, opoziție și
              portabilitate, precum și dreptul de a depune plângere la Autoritatea Națională de Supraveghere
              a Prelucrării Datelor cu Caracter Personal.
            </p>
            {contact === null ? (
              <p className="text-ink-muted">
                Pentru această piață nu este configurat un canal de contact pentru cereri privind datele.
              </p>
            ) : (
              <p>Cererile se trimit la {contact}.</p>
            )}
          </Article>

          <Article title="Stocare în browser">
            <p>
              Aplicația păstrează în browserul tău adresa, coșul și codurile comenzilor recente, ca să nu le
              reintroduci la fiecare vizită. Aceste date rămân pe dispozitivul tău și le poți șterge din
              setările browserului.
            </p>
          </Article>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <SectionHeading level={1}>{t("legal.terms_title", "Termeni și condiții")}</SectionHeading>

          <Article title="Cine îți vinde">
            <p>
              {platformName} este o platformă de intermediere. <strong>Vânzătorul este restaurantul</strong>{" "}
              care pregătește comanda: el încheie contractul cu tine, emite bonul fiscal și răspunde pentru
              produse. Denumirea, CUI-ul, sediul și telefonul restaurantului îți sunt afișate în ecranul de
              finalizare, înainte de butonul de confirmare.
            </p>
            <p>
              Când comanda ta se împarte între mai multe restaurante, ți se afișează toți vânzătorii înainte
              de confirmare și primești câte un document fiscal de la fiecare.
            </p>
          </Article>

          <Article title="Comanda">
            <p>
              Contractul se încheie în momentul în care confirmi comanda și restaurantul o acceptă. Până la
              acceptare, comanda poate fi refuzată — de exemplu dacă un produs s-a terminat — iar tu ești
              anunțat.
            </p>
          </Article>

          <Article title="Prețuri și taxa de livrare">
            <p>
              Prețurile afișate includ TVA și sunt cele pe care le plătești. Taxa de livrare apare ca linie
              distinctă pe bonul fiscal emis de restaurant, cu cota standard de TVA aplicabilă serviciilor de
              transport. Când comanda se împarte între mai mulți vânzători, taxa de livrare este o singură
              prestație și apare integral pe unul dintre documente.
            </p>
          </Article>

          <Article title="Plata">
            <p>
              Plata se face <strong>numerar, la livrare</strong>. Curierul încasează în numele restaurantului
              vânzător, în baza unui mandat, și îți predă bonul fiscal odată cu comanda. Nu se percepe și nu
              se solicită bacșiș prin aplicație.
            </p>
          </Article>

          <Article title="Livrare și estimări">
            <p>
              Intervalul de livrare afișat este o estimare, calculată din timpul de preparare și din traseu.
              Îl comparăm cu ora reală de livrare și îți arătăm ambele valori după finalizarea comenzii.
            </p>
          </Article>

          <Article title="Refuzul la ușă">
            <p>
              Dacă refuzi comanda la livrare, curierul readuce produsele și bonul fiscal la restaurant, iar
              anularea se face pe baza bonului returnat. Refuzurile repetate și nejustificate pot duce la
              limitarea accesului la platformă.
            </p>
          </Article>

          <Article title="Dreptul de retragere">
            <p>
              Mâncarea preparată este un produs perisabil, livrat gata pregătit. Dreptul legal de retragere
              în 14 zile, aplicabil vânzărilor la distanță, nu se aplică acestor produse. Asta nu îți
              afectează drepturile în caz de produs neconform: reclamația se rezolvă cu restaurantul
              vânzător, cu sprijinul platformei.
            </p>
          </Article>

          <Article title="Factură pe firmă">
            <p>
              Poți cere factură pe firmă la finalizarea comenzii. Datele completate ajung la restaurantul
              vânzător, care emite factura conform obligațiilor lui legale.
            </p>
          </Article>

          <Article title="Reclamații">
            {contact === null ? (
              <p className="text-ink-muted">
                Pentru această piață nu este configurat un canal de contact pentru reclamații.
              </p>
            ) : (
              <p>
                Reclamațiile se trimit la {contact}, cu codul comenzii. Codul comenzii rămâne același pe tot
                parcursul ei și îl găsești în pagina de urmărire.
              </p>
            )}
          </Article>
        </div>
      )}
    </div>
  );
}
